import {
  parseDateString,
  getCustomDateRange,
  getMonthDateRange,
  getWeekDateRange,
  getYearDateRange,
} from './dateUtils';

/**
 * The period an AI parse resolved, as Date objects at LOCAL midnight.
 *
 * Two traps, both of which shipped once:
 *
 * The API sends `date_range: {start, end}` — the names
 * `schemas.AiFilterDateRange` defines. Reading `start_date` / `end_date` threw
 * away every period the model had understood, so the period box said
 * "couldn't read that" for a date the server had resolved correctly.
 *
 * And the dates have to be parsed, not passed on as strings:
 * `new Date("2026-02-05")` is UTC midnight, which west of Greenwich is the
 * *previous* day once `formatDateString` reads it back with getDate(). "De 5
 * febrero a 2 de marzo" arrived correctly and was shown as Feb 4 - Mar 1.
 *
 * Pure module: no React, no network.
 */
export const parsedPeriod = (result) => {
  const range = result?.date_range;
  if (!range?.start || !range?.end) return null;
  return {
    start: parseDateString(range.start),
    end: parseDateString(range.end),
  };
};


const lastDayOf = (year, month) => new Date(year, month + 1, 0).getDate();

/**
 * The date range a parsed period becomes.
 *
 * A period that happens to be exactly one calendar unit becomes that unit,
 * not a custom range: "2023" is the Year view of 2023, with the arrows moving
 * to 2022 and 2024, rather than a custom Jan 1 - Dec 31 whose arrows do
 * nothing. Anything that does not line up stays custom, which is the honest
 * answer for "abril 2023 to june 3".
 */
export const periodToDateRange = (period) => {
  if (!period?.start || !period?.end) return null;
  const { start, end } = period;

  const sameYear = start.getFullYear() === end.getFullYear();
  const startsOnFirst = start.getDate() === 1;

  if (sameYear && startsOnFirst && start.getMonth() === 0
      && end.getMonth() === 11 && end.getDate() === 31) {
    return getYearDateRange(start.getFullYear());
  }

  if (sameYear && startsOnFirst && start.getMonth() === end.getMonth()
      && end.getDate() === lastDayOf(end.getFullYear(), end.getMonth())) {
    return getMonthDateRange(start.getFullYear(), start.getMonth());
  }

  // Monday to Sunday, the week this app draws.
  const sevenDays = (end - start) / 86400000 === 6;
  if (sevenDays && start.getDay() === 1) {
    return getWeekDateRange(start);
  }

  return getCustomDateRange(start, end);
};

export default parsedPeriod;
